import React, { useEffect, useState } from "react";
import { useAtom } from "jotai";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
} from "@mui/material";
import { parseISO, format } from "date-fns";
import {
  getAllActiveSessions,
  endSessionById,
} from "../../services/VcatUserSession/service";
import { ActiveSessionWithUser } from "../../types/models/vcatSession";
import {
  accountRootsAtom,
  fetchAccountRootsAtom,
} from "../../state/atoms/accountRootsAtom";

type SessionWithRoot = ActiveSessionWithUser & {
  account_root_id: number | null;
};

const AllActiveSessionsPage: React.FC = () => {
  const [sessions, setSessions] = useState<SessionWithRoot[]>([]);
  const [loading, setLoading] = useState(true);
  const [endingId, setEndingId] = useState<number | null>(null);

  const [accountRoots] = useAtom(accountRootsAtom);
  const [, fetchAccountRoots] = useAtom(fetchAccountRootsAtom);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      await fetchAccountRoots();

      const data = await getAllActiveSessions();
      setSessions(data as SessionWithRoot[]);
    } catch (e) {
      console.error("Failed to fetch all active sessions:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const handleEndSession = async (sessionId: number) => {
    setEndingId(sessionId);
    try {
      await endSessionById(sessionId);
      await fetchSessions();
    } catch (e) {
      console.error("Failed to end session:", e);
    } finally {
      setEndingId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress sx={{ color: "#fff" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, backgroundColor: "transparent", minHeight: "100vh" }}>
      <Typography variant="h4" sx={{ color: "#FFFFFF", mb: 3 }}>
        All Active Sessions
      </Typography>

      <Paper sx={{ p: 2, backgroundColor: "transparent", borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "#A8B2D1" }}>User</TableCell>
              <TableCell sx={{ color: "#A8B2D1" }}>Email</TableCell>
              <TableCell sx={{ color: "#A8B2D1" }}>Phone</TableCell>
              <TableCell sx={{ color: "#A8B2D1" }}>Serial</TableCell>
              <TableCell sx={{ color: "#A8B2D1" }}>Account Root</TableCell>
              <TableCell sx={{ color: "#A8B2D1" }}>Started</TableCell>
              <TableCell sx={{ color: "#A8B2D1" }}>Last Active</TableCell>
              <TableCell sx={{ color: "#A8B2D1" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map(
              ({
                session,
                username,
                email,
                phone,
                account_root_id,
              }) => {
                const root = accountRoots.find((r) => r.id === account_root_id);
                const rootName = root ? root.name : "—";

                return (
                  <TableRow key={session.id}>
                    <TableCell sx={{ color: "#FFFFFF" }}>
                      {username}
                    </TableCell>
                    <TableCell sx={{ color: "#FFFFFF" }}>
                      {email ?? "—"}
                    </TableCell>
                    <TableCell sx={{ color: "#FFFFFF" }}>
                      {phone ?? "—"}
                    </TableCell>
                    <TableCell sx={{ color: "#FFFFFF" }}>
                      {session.serial}
                    </TableCell>
                    <TableCell sx={{ color: "#FFFFFF" }}>
                      {rootName}
                    </TableCell>
                    <TableCell sx={{ color: "#FFFFFF" }}>
                      {format(
                        parseISO(session.start_time),
                        "yyyy-MM-dd HH:mm:ss"
                      )}
                    </TableCell>
                    <TableCell sx={{ color: "#FFFFFF" }}>
                      {format(
                        parseISO(session.last_ping),
                        "yyyy-MM-dd HH:mm:ss"
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleEndSession(session.id)}
                        disabled={endingId === session.id}
                      >
                        {endingId === session.id ? "Ending..." : "End"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              }
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default AllActiveSessionsPage;
